import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Layout, Spin, message } from "antd";
import { useAuth } from "../../../hooks/useAuth";
import { useOrganizationTree } from "./hooks/useOrganizationTree";
import { organizationAPI } from "../../../api/organizationApi";
import OrgSidebar from "./components/OrgSidebar";
import MemberWorkspace from "./components/MemberWorkspace";
import RightInspector from "./components/RightInspector";
import OrganizationModal from "./components/OrganizationModal";
import MoveOrganizationModal from "./components/MoveOrganizationModal";
import BatchTransferModal from "./components/BatchTransferModal";
import BatchActionsBar from "../../../components/common/BatchActionsBar";
import { MODAL_TYPES } from "./constants/organizationConstants";

const OrganizationManagementPage = () => {
  const { hasPermission } = useAuth();
  const {
    loading: treeLoading,
    organizationTree,
    organizationFlatList,
    loadOrganizationTree,
    createOrganization,
    updateOrganization,
    deleteOrganization,
  } = useOrganizationTree();

  const canCreate = hasPermission("user:create");
  const canUpdate = hasPermission("user:update");
  const canDelete = hasPermission("user:delete");
  const canRead = hasPermission("user:read");

  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [inspectorTarget, setInspectorTarget] = useState(null);
  const [memberCounts, setMemberCounts] = useState({});

  const [orgModalVisible, setOrgModalVisible] = useState(false);
  const [orgModalType, setOrgModalType] = useState(MODAL_TYPES.CREATE);
  const [orgModalContext, setOrgModalContext] = useState(null);
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [batchTransferVisible, setBatchTransferVisible] = useState(false);
  const [batchTransferMode, setBatchTransferMode] = useState("transfer");

  const selectedOrg = useMemo(
    () => organizationFlatList.find((o) => o.id === selectedOrgId) || null,
    [selectedOrgId, organizationFlatList]
  );

  useEffect(() => {
    if (canRead) loadOrganizationTree();
  }, [canRead, loadOrganizationTree]);

  // 加载各组织直接成员数
  useEffect(() => {
    if (!organizationFlatList.length) return;
    let cancelled = false;
    const loadCounts = async () => {
      const counts = {};
      await Promise.all(
        organizationFlatList.map(async (org) => {
          try {
            const res = await organizationAPI.getOrganizationUsers(org.id);
            if (!cancelled) counts[org.id] = (res.items || []).length;
          } catch {
            if (!cancelled) counts[org.id] = 0;
          }
        })
      );
      if (!cancelled) setMemberCounts(counts);
    };
    loadCounts();
    return () => { cancelled = true; };
  }, [organizationFlatList]);

  const handleCreateOrg = useCallback((parentOrg = null) => {
    setOrgModalType(MODAL_TYPES.CREATE);
    setOrgModalContext(parentOrg);
    setOrgModalVisible(true);
  }, []);

  const handleEditOrg = useCallback((org) => {
    setOrgModalType(MODAL_TYPES.EDIT);
    setOrgModalContext(org);
    setOrgModalVisible(true);
  }, []);

  const handleMoveOrg = useCallback((org) => {
    setOrgModalContext(org);
    setMoveModalVisible(true);
  }, []);

  const handleDeleteOrg = useCallback(
    async (orgId) => {
      await deleteOrganization(orgId);
      if (selectedOrgId === orgId) {
        setSelectedOrgId(null);
        setSelectedUserIds([]);
        setInspectorTarget(null);
      }
    },
    [deleteOrganization, selectedOrgId]
  );

  const handleOrgSubmit = useCallback(
    async (values) => {
      if (orgModalType === MODAL_TYPES.CREATE) {
        const result = await createOrganization(values);
        const newId = result?.data?.id || result?.id;
        if (newId) {
          setSelectedOrgId(newId);
          setInspectorTarget({ type: "org", id: newId });
        }
      } else {
        await updateOrganization(orgModalContext.id, values);
      }
      setOrgModalVisible(false);
    },
    [orgModalType, createOrganization, updateOrganization, orgModalContext]
  );

  const handleMoveSubmit = useCallback(
    async (orgId, targetParentId) => {
      await updateOrganization(orgId, { parent_id: targetParentId });
      setMoveModalVisible(false);
    },
    [updateOrganization]
  );

  const handleSelectOrg = useCallback((orgId) => {
    setSelectedOrgId(orgId);
    setSelectedUserIds([]);
    setInspectorTarget(orgId ? { type: "org", id: orgId } : null);
  }, []);

  const handleSelectUsers = useCallback(
    (userIds) => {
      setSelectedUserIds(userIds);
      if (userIds.length === 1) {
        setInspectorTarget({ type: "user", id: userIds[0] });
      } else {
        setInspectorTarget(
          selectedOrgId ? { type: "org", id: selectedOrgId } : null
        );
      }
    },
    [selectedOrgId]
  );

  const handleInspect = useCallback((type, id) => {
    setInspectorTarget({ type, id });
  }, []);

  const handleOpenBatchTransfer = useCallback((mode) => {
    setBatchTransferMode(mode);
    setBatchTransferVisible(true);
  }, []);

  if (!canRead) {
    return (
      <div style={{ padding: 50, textAlign: "center" }}>
        您没有权限查看组织管理
      </div>
    );
  }

  return (
    <Spin spinning={treeLoading} style={{ height: "100%" }}>
      <Layout style={{ height: "calc(100vh - 140px)", background: "#fff" }}>
        <Layout.Sider
          width={280}
          style={{
            background: "#fafafa",
            borderRight: "1px solid #f0f0f0",
            overflow: "auto",
          }}
        >
          <OrgSidebar
            tree={organizationTree}
            flatList={organizationFlatList}
            selectedOrgId={selectedOrgId}
            onSelect={handleSelectOrg}
            onCreate={handleCreateOrg}
            onEdit={handleEditOrg}
            onMove={handleMoveOrg}
            onDelete={handleDeleteOrg}
            canCreate={canCreate}
            canUpdate={canUpdate}
            canDelete={canDelete}
            memberCounts={memberCounts}
          />
        </Layout.Sider>
        <Layout.Content
          style={{
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minWidth: 0,
          }}
        >
          <MemberWorkspace
            selectedOrg={selectedOrg}
            selectedUserIds={selectedUserIds}
            onSelectUsers={handleSelectUsers}
            onInspect={handleInspect}
            onReloadTree={loadOrganizationTree}
            canUpdate={canUpdate}
            canDelete={canDelete}
            organizationTree={organizationTree}
            organizationFlatList={organizationFlatList}
            onOpenBatchTransfer={handleOpenBatchTransfer}
          />
        </Layout.Content>
        <Layout.Sider
          width={360}
          style={{
            background: "#fafafa",
            borderLeft: "1px solid #f0f0f0",
            overflow: "auto",
          }}
        >
          <RightInspector
            target={inspectorTarget}
            selectedOrg={selectedOrg}
            selectedUserIds={selectedUserIds}
            organizationFlatList={organizationFlatList}
            onInspect={handleInspect}
            onReloadTree={loadOrganizationTree}
            canUpdate={canUpdate}
          />
        </Layout.Sider>
      </Layout>

      {selectedUserIds.length > 0 && (
        <BatchActionsBar
          selectedCount={selectedUserIds.length}
          itemName="个成员"
          onClearSelection={() => setSelectedUserIds([])}
          actions={[
            canUpdate && {
              key: "batch-transfer",
              label: "批量换组织",
              onClick: () => handleOpenBatchTransfer("transfer"),
            },
            canUpdate && {
              key: "batch-default",
              label: "批量设默认",
              onClick: () => handleOpenBatchTransfer("setDefault"),
            },
            canUpdate && {
              key: "batch-remove",
              label: "批量移除",
              danger: true,
              onClick: () => handleOpenBatchTransfer("remove"),
            },
          ].filter(Boolean)}
        />
      )}

      <OrganizationModal
        visible={orgModalVisible}
        modalType={orgModalType}
        selectedOrganization={orgModalContext}
        organizationTree={organizationTree}
        onCancel={() => setOrgModalVisible(false)}
        onSubmit={handleOrgSubmit}
      />
      <MoveOrganizationModal
        visible={moveModalVisible}
        selectedOrganization={orgModalContext}
        organizationTree={organizationTree}
        onCancel={() => setMoveModalVisible(false)}
        onConfirm={handleMoveSubmit}
      />
      <BatchTransferModal
        visible={batchTransferVisible}
        mode={batchTransferMode}
        selectedUserIds={selectedUserIds}
        selectedOrg={selectedOrg}
        organizationFlatList={organizationFlatList}
        onCancel={() => setBatchTransferVisible(false)}
        onSuccess={() => {
          setBatchTransferVisible(false);
          setSelectedUserIds([]);
          loadOrganizationTree();
        }}
        canUpdate={canUpdate}
      />
    </Spin>
  );
};

export default OrganizationManagementPage;
